/**
 * The **Email** tab of `/integrations`: the Amazon SES account Wend sends a
 * tenant's mail through, and — behind Advanced — what it is allowed to put in
 * that mail.
 *
 * Everything below the line "the SES card" used to live in `IntegrationsPage`,
 * which rendered it, then the tracking card, then the WhatsApp card, one under
 * the other. That page's argument for the order was sound and is preserved in
 * its own header; what it did not survive was arithmetic. Three stacked cards
 * meant connecting WhatsApp began with scrolling past every SES panel and the
 * whole of tracking, and the page had accreted to 4,447 lines across the
 * directory. The channels are tabs now, and this file is one of them.
 *
 * ## The order on this tab is a claim about failure, not about importance
 *
 * **The screen exists to get one thing right, and it is not the happy path.**
 * Credentials that authenticate are not an account that can send. An SES
 * account still in the sandbox delivers to verified addresses and rejects
 * everything else; an account with no verified identity has nothing to send
 * *from*; an account whose only verified sender AWS has paused delivers
 * nothing; an account AWS has paused entirely sends nothing at all. All of
 * those authenticate perfectly. A tenant who finds out about any of them when a
 * campaign reaches nobody has been failed by this screen.
 *
 * So every one of those is a `Callout` with the account's own numbers in it,
 * above the fold, never behind a disclosure, and — for the sandbox — with the
 * way out attached. The sandbox one is the reason the rule is written down: a
 * sandboxed account reaching nobody is the single most common "the product is
 * broken" report, and a campaign sent from one *looks like it ran*.
 *
 * Behind **Advanced** goes the other job this page does, which has a completely
 * different frequency: tracking, the redirect subdomain, VDM and the SNS topic
 * are decisions about an account that already works. `AdvancedDisclosure`'s
 * header carries that argument and the rule that moving copy behind it is not a
 * licence to shorten it.
 *
 * **One line crosses that boundary, and it is a statement rather than a
 * control.** Collapsing the tracking card took the *answer* down with the
 * switches: `AdvancedDisclosure`'s closed summary is a constant string, so a
 * workspace pixelling every recipient and one pixelling none rendered the
 * identical grey strip. Whether a pixel is in every message is not an expert
 * setting — it is what every recipient receives, and the tenant who most needs
 * it at a glance is the one who told their members it was not happening. So
 * `TrackingStatement` prints it above the disclosure and every switch stays
 * inside. The tab makes no request for it: the card reports what it is already
 * rendering, the same seam a panel uses to report its badge to the tab rail, so
 * there is one derivation and not two accounts of a tenant's mail on one screen.
 *
 * **Advanced is rendered whatever the connection is doing, including when this
 * tab could not load at all.** The load failing is a fact about one request,
 * not about the tracking settings, and an error state that swallowed the
 * disclosure would make four working panels unreachable because a different
 * endpoint was down. The connection surface owns its own error, and nothing
 * else on the tab is inside it.
 *
 * ## What has not changed
 *
 * **Every figure here came from the API on this request.** The quota, the send
 * rate, what has been sent, which identities exist and whether they are
 * verified are all read out of the check result. Nothing is defaulted,
 * remembered from AWS's documentation, or computed from a plausible assumption
 * — a number nobody measured is worse on this screen than no number, because it
 * is a number somebody will plan a send around.
 *
 * **The verdict is `sesReadiness()` from `@wend/shared`,** read through
 * `readSesIntegration`. The API calls it to set the row's status and, in slice
 * 5, to decide whether a campaign may be scheduled at all. Deriving it a second
 * time here is how the two end up disagreeing about what "ready" means — and
 * the tab's own status mark is the *same* `reading.badge` object this card
 * renders, for the same reason one layer down.
 *
 * **SECURITY.** The secret access key exists in this file for the length of one
 * submission and in one place: the value of a `type="password"` input, which is
 * cleared the moment the request it was typed for has been answered. It is
 * never put in a URL, a query string, a toast, an error message or a log line,
 * it is never read back from the server — the read contract has no field it
 * could arrive in — and nothing on this screen renders it. What comes back is
 * the region and the last four of the access key ID, which is the whole of what
 * a person needs to recognise which key is connected.
 */
import {
  SES_ACCOUNT_NOTHING_READABLE_COPY,
  SES_ACCOUNT_NOT_VISIBLE_COPY,
  SES_PRODUCTION_ACCESS_URL,
  connectSesRequestSchema,
  type SesIntegration,
  type SesCheckFailure,
  type SesCheckResult,
  type SesIdentity,
  type SesTransport,
} from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Kicker,
  Spinner,
  buttonVariants,
  useToast,
} from '@wend/ui';
import { ExternalLink, KeyRound, Mail, RotateCw, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { SesReadinessCallouts } from '../../components/SesReadinessCallouts';
import { ROOT_FIELD, apiFieldErrors, errorMessage } from '../../lib/api/errors';
import {
  checkSes,
  connectSes,
  disconnectSes,
  fetchSesIntegration,
  type SesCheckResponse,
} from '../../lib/api/integrations';
import { formatDateTime, pluralise } from '../../lib/format';
import {
  SES_FAILURE_HEADING,
  SES_FAILURE_HEADING_STORED,
  SES_READINESS_DISPLAY,
  SES_SENDING_PAUSED,
  SES_STUB_NOTICE,
  describeSandboxLimits,
  formatDailyQuota,
  formatSendRate,
  formatSentLast24Hours,
  identityTypeLabel,
  readSesIntegration,
} from '../../lib/integration-display';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { useAsyncData } from '../../lib/use-async-data';
import { AdvancedDisclosure } from './AdvancedDisclosure';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';
import { TrackingCard } from './TrackingCard';
import { useReportIntegrationTabStatus } from './integration-tabs';
import type { TrackingStateLine } from './tracking-display';

/** What a check learned, when it was this session's own check that learned it. */
type LocalOutcome = SesCheckResponse['outcome'];

export function EmailTab() {
  /**
   * The load answers with a wrapper object rather than `SesIntegration | null`, so
   * that "not loaded yet" and "loaded, nothing connected" are different values.
   * Both are legitimately null on the inside, and a screen that conflated them
   * would show the connect form to someone whose request had failed.
   */
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData(
    async (signal) => ({ integration: await fetchSesIntegration(signal) }),
    [],
  );

  /**
   * The integration as this screen has changed it since the server last spoke,
   * and the outcome of the check that changed it.
   *
   * Connect and test both answer with the whole row *and* the outcome, so
   * neither has to re-read — and re-reading would lose the outcome, which is the
   * only carrier of which transport answered and of the failure code. Both are
   * cleared whenever a fresh copy arrives, so the server always wins in the end.
   */
  const [patched, setPatched] = useState<{ integration: SesIntegration | null } | null>(null);
  const [outcome, setOutcome] = useState<LocalOutcome | null>(null);

  /**
   * What tracking is doing to this workspace's mail, as the card behind Advanced
   * reports it. `null` until it knows, and again if its load fails.
   *
   * The tab asks no question of its own about tracking — see `TrackingCard`'s
   * `onStateChange`. `setTrackingState` is a `useState` setter and therefore
   * stable for the life of the tab, which is what keeps the card's reporting
   * effect from firing on every render.
   */
  const [trackingState, setTrackingState] = useState<TrackingStateLine | null>(null);
  useEffect(() => {
    setPatched(null);
    setOutcome(null);
  }, [data]);

  const current = patched ?? data;

  /**
   * The reading, or null while there is nothing to read it from.
   *
   * `readSesIntegration(null)` is a real answer — "not connected" — and is
   * deliberately not used to stand in for a failed load: a request that never
   * came back tells us nothing about whether an account is connected, and a tab
   * that badged it "Not connected" would be inventing a state. Hence the null.
   */
  const reading = current === null ? null : readSesIntegration(current.integration);

  // The same badge the card header renders. See `integration-tabs.tsx`.
  useReportIntegrationTabStatus('email', reading?.badge ?? null);

  function applyCheck(response: SesCheckResponse): void {
    setPatched({ integration: response.integration });
    setOutcome(response.outcome);
  }

  return (
    <div className="flex flex-col gap-6">
      {loadingFirst ? (
        <Card>
          <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Checking what this workspace has connected…</span>
          </CardContent>
        </Card>
      ) : current === null || reading === null ? (
        <Card>
          <ErrorState error={error} title="Could not load your integrations" onRetry={reload} />
        </Card>
      ) : (
        <>
          {error !== null && (
            <FormAlert tone="error">
              {errorMessage(error)} What is shown below is the last version that loaded.
            </FormAlert>
          )}

          {current.integration === null ? (
            <Card>
              <CardHeader>
                <SesEyebrow badge={<Badge tone={reading.badge.tone}>{reading.badge.label}</Badge>} />
                <CardTitle>Amazon SES</CardTitle>
                <CardDescription>
                  Connect the AWS account Wend should send your email through. Nothing can be sent
                  by email until this is connected.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConnectForm existing={null} onConnected={applyCheck} />
              </CardContent>
            </Card>
          ) : (
            <ConnectedSes
              integration={current.integration}
              outcome={outcome}
              refreshing={refreshing}
              onChecked={applyCheck}
              onDisconnected={() => {
                setPatched({ integration: null });
                setOutcome(null);
              }}
            />
          )}
        </>
      )}

      {/*
        Outside every branch above, including the error one — see the header.
        `TrackingCard` composes the SNS topic panel (which is where the webhook
        URL lives), the redirect subdomain panel and VDM, so the whole expert
        surface moves as one piece and none of its copy was touched on the way.

        The statement above it is not a control and is not a summary of the
        disclosure's contents — the `summary` string is that. It is the one fact
        behind this disclosure that a person may have made a promise about, and
        it is stated outside the disclosure for the reason the header gives.
      */}
      <TrackingStatement state={trackingState} />
      <AdvancedDisclosure summary="Open and click tracking, redirect subdomain, Virtual Deliverability Manager, SNS topic and webhook URL">
        <TrackingCard onStateChange={setTrackingState} />
      </AdvancedDisclosure>
    </div>
  );
}

/**
 * Whether this workspace's mail carries tracking, in one line, above the fold.
 *
 * **This is the one thing on this tab that is above the fold without affecting
 * whether you can send,** so it is worth saying why the dividing line bends
 * here. The line sorts *controls* — a switch, a domain, a topic — by whether
 * getting them wrong is why a campaign reached nobody, and by that test every
 * tracking control belongs behind Advanced, which is where they are and where
 * they stay. This is not a control. It is a statement of what every recipient
 * receives, and the person who needs it is not configuring anything: they told
 * their members that Wend does not track them and they have come here to check
 * that it is still true. `AdvancedDisclosure`'s closed summary is a constant
 * string, so before this line existed that page looked identical whether the
 * answer was yes or no.
 *
 * **Nothing known renders nothing**, never a reassuring default. A load still in
 * flight and a load that failed both report `null`, and "we have not read your
 * settings" is not "nothing is added to your messages" — the card inside owns
 * its own error state and says so there.
 */
function TrackingStatement({ state }: { state: TrackingStateLine | null }) {
  if (state === null) return null;

  return (
    <p className="flex max-w-prose flex-wrap items-baseline gap-x-3 gap-y-1 text-[0.88rem] leading-relaxed">
      <Kicker as="span">
        Tracking
      </Kicker>
      <Badge tone={state.badge.tone}>{state.badge.label}</Badge>
      <span className="text-muted">{state.says}</span>
    </p>
  );
}

/**
 * The eyebrow every SES card wears: the channel's glyph, and where the account
 * stands.
 *
 * It used to spell out "Email · Amazon SES" beside them. The tab above now says
 * Email and the card title below says Amazon SES, so the line was saying both
 * of its words twice — the kind of duplication that arrives when a card has to
 * introduce itself because nothing above it does.
 */
function SesEyebrow({ badge }: { badge: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
        <Mail aria-hidden="true" className="size-4" />
      </span>
      {badge}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Connected                                                                   */
/* -------------------------------------------------------------------------- */

function ConnectedSes({
  integration,
  outcome,
  refreshing,
  onChecked,
  onDisconnected,
}: {
  integration: SesIntegration;
  outcome: LocalOutcome | null;
  refreshing: boolean;
  onChecked: (response: SesCheckResponse) => void;
  onDisconnected: () => void;
}) {
  const { toast } = useToast();
  const reading = readSesIntegration(integration);
  const [replacing, setReplacing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testFailure, setTestFailure] = useState<RequestFailure | null>(null);

  /** The failure this session actually saw, which carries a code and a transport. */
  const liveFailure: SesCheckFailure | null =
    outcome !== null && !outcome.ok ? outcome.failure : null;

  /**
   * Which transport answered. The current outcome wins when there is one — after
   * a failed check the stored snapshot is stale or absent, and it is precisely
   * the failed check whose provenance matters most.
   */
  const transport: SesTransport | null =
    outcome !== null
      ? outcome.ok
        ? outcome.result.transport
        : outcome.failure.transport
      : (reading.check?.transport ?? null);

  async function onTest(): Promise<void> {
    if (testing) return;

    setTestFailure(null);
    setTesting(true);

    try {
      const response = await checkSes();
      onChecked(response);
      toast(
        response.outcome.ok
          ? { tone: 'success', title: 'Amazon SES answered', description: 'The card below is what it said.' }
          : {
              tone: 'error',
              title: 'Amazon SES refused the check',
              description: SES_FAILURE_HEADING[response.outcome.failure.code],
            },
      );
    } catch (error) {
      setTestFailure(requestFailure(error));
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <SesEyebrow badge={<Badge tone={reading.badge.tone}>{reading.badge.label}</Badge>} />
          <CardTitle>Amazon SES</CardTitle>
          <CardDescription>
            Connected in{' '}
            <span className="font-mono text-[0.85rem] text-text">{integration.region}</span> with the
            access key ending{' '}
            <span className="font-mono text-[0.85rem] text-text">
              {integration.accessKeyIdLast4}
            </span>
            .{' '}
            {integration.lastVerifiedAt === null
              ? 'These credentials have never passed a check.'
              : `Last verified ${formatDateTime(integration.lastVerifiedAt)}.`}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-5">
          {testFailure !== null && <RequestFailureAlert failure={testFailure} />}

          {/* The failure leads. A stale green snapshot is shown further down and
              labelled as history — never as the current state. */}
          {reading.failing && (
            <Callout
              tone="bad"
              // Announced only when this session caused it. A card that loads
              // already failing is met in reading order like any other prose;
              // firing `alert` into page-load chatter is how an announcement
              // gets lost or read twice.
              live={liveFailure === null ? 'off' : 'assertive'}
              title={
                liveFailure === null
                  ? SES_FAILURE_HEADING_STORED
                  : SES_FAILURE_HEADING[liveFailure.code]
              }
            >
              <p>
                {liveFailure?.message ??
                  integration.lastError ??
                  'Amazon SES refused the last check and no reason was recorded. Test again to find out why.'}
              </p>
              <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                Last tried {formatDateTime(liveFailure?.checkedAt ?? integration.updatedAt)}
              </p>
            </Callout>
          )}

          {/* Every flag that is set, not just the headline state — an account can
              be sandboxed *and* have nothing to send from, and a card that showed
              only the verdict would hide one of the two. */}
          {/* Extracted in slice 30 so a verify suite can RENDER these branches.
              `EmailTab` reaches `lib/api/` and cannot be rendered in a suite; the
              `!null` trap these conditions defend against is the one the Overview
              card used to hold about thirty rendered assertions on, and that card
              is gone. See `SesReadinessCallouts` for the trap itself. */}
          {!reading.failing && reading.readiness !== null && reading.check !== null && (
            <SesReadinessCallouts readiness={reading.readiness} check={reading.check} />
          )}

          {!reading.failing && reading.check === null && (
            <Callout tone="warn" title="These credentials have not been checked yet">
              Nothing is known about this AWS account — not its quota, not whether it is out of the
              sandbox, and not whether it has anything to send from. Test the connection to find out.
            </Callout>
          )}

          {transport === 'stub' && (
            <Callout tone="info">
              <span className="text-text">{SES_STUB_NOTICE}</span>
            </Callout>
          )}

          {reading.check !== null && (
            <CheckSnapshot
              integration={integration}
              check={reading.check}
              stale={reading.stale}
              // `?? null`, never `?? 0`. Zero says the account has no verified
              // sender; null says nobody was allowed to count.
              verifiedIdentityCount={reading.readiness?.verifiedIdentityCount ?? null}
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={testing}
              leadingIcon={<RotateCw aria-hidden="true" />}
              onClick={() => {
                void onTest();
              }}
            >
              {testing ? 'Asking Amazon' : 'Test connection'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={testing || replacing}
              leadingIcon={<KeyRound aria-hidden="true" />}
              onClick={() => {
                setReplacing(true);
              }}
            >
              Replace credentials
            </Button>

            {refreshing && <Spinner size="sm" label="Reloading the integration" />}

            <p className="text-[0.85rem] text-muted">
              Testing asks AWS again, on your account, with the key already stored here.
            </p>
          </div>
        </CardContent>
      </Card>

      {replacing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[1rem]">Replace these credentials</CardTitle>
            {/*
              The reason there is no partial update — a half-updated credential
              fails at send time rather than here — is a decision, and decisions
              live in a header. What a person needs on screen is the shape of
              the form in front of them.
            */}
            <CardDescription>
              There is no partial update: a new key, a new secret and a region replace what is
              stored, all three together — including when the only thing you meant to change is the
              region.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ConnectForm
              existing={integration}
              onConnected={(response) => {
                setReplacing(false);
                onChecked(response);
              }}
              onCancel={() => {
                setReplacing(false);
              }}
            />
          </CardContent>
        </Card>
      )}

      <DisconnectPanel integration={integration} onDisconnected={onDisconnected} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* What Amazon reported                                                        */
/* -------------------------------------------------------------------------- */

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-t border-line py-3">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[0.92rem] text-text">{children}</dd>
    </div>
  );
}

/**
 * The account as Amazon last described it.
 *
 * It stays above the fold rather than going behind Advanced, and the dividing
 * line says why: the quota, the enforcement status and the identity list are
 * not settings, they are the evidence for whether this account can send — and
 * "I started verifying that domain and never finished" is the most common
 * reason an account that looks set up cannot.
 *
 * `stale` is the whole reason this is a component rather than a block of markup.
 * A re-check that fails leaves the previous success in place on purpose — "not
 * working now, last verified on Tuesday" is more useful than an empty panel —
 * but a quota rendered beside a broken connection as though both were current is
 * a screen lying by adjacency. When it is history, it says so first.
 */
function CheckSnapshot({
  integration,
  check,
  stale,
  verifiedIdentityCount,
}: {
  integration: SesIntegration;
  check: SesCheckResult;
  stale: boolean;
  /** `null` when `ses:ListEmailIdentities` was refused — never defaulted to 0. */
  verifiedIdentityCount: number | null;
}) {
  return (
    <section className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <Kicker as="h3" rule>
        {stale ? 'What Amazon reported when this last worked' : 'What Amazon SES reported'}
      </Kicker>

      <p className="mt-1.5 max-w-prose text-[0.85rem] leading-relaxed text-muted">
        {stale ? (
          <>
            These figures are from {formatDateTime(check.checkedAt)}, the last check that succeeded.
            The credentials are failing now — see above — so treat everything below as history
            rather than as the state of the account today.
          </>
        ) : (
          <>Checked {formatDateTime(check.checkedAt)}. Every figure below came from Amazon.</>
        )}
      </p>

      <dl className="mt-3 grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
        <Detail label="Region">
          <span className="font-mono text-[0.88rem]">{integration.region}</span>
        </Detail>
        <Detail label="Access key ID">
          <span className="font-mono text-[0.88rem]">
            <span className="text-muted">ends </span>
            {integration.accessKeyIdLast4}
          </span>
        </Detail>
        {/*
          Four rows that all come from `GetAccount`, and all four are absent
          together when the key cannot call it. "Not readable" is a different
          row from a value — a ternary here would print "Sandbox" and "Paused
          by AWS" over an account AWS was never asked about, which is the
          loudest possible way to be wrong about somebody's infrastructure.
        */}
        <Detail label="Account access">
          {check.productionAccess === null ? (
            <span className="text-muted">Not readable</span>
          ) : check.productionAccess ? (
            'Production'
          ) : (
            'Sandbox'
          )}
        </Detail>
        <Detail label="Sending">
          {check.sendingEnabled === null ? (
            <span className="text-muted">Not readable</span>
          ) : check.sendingEnabled ? (
            'Enabled'
          ) : (
            'Paused by AWS'
          )}
        </Detail>
        <Detail label="Enforcement status">
          {check.enforcementStatus === null ? (
            <span className="text-muted">Not reported</span>
          ) : (
            <span className="font-mono text-[0.88rem]">{check.enforcementStatus}</span>
          )}
        </Detail>
        <Detail label="Daily quota">
          {check.quota === null ? (
            <span className="text-muted">Not readable</span>
          ) : (
            formatDailyQuota(check.quota.max24HourSend)
          )}
        </Detail>
        <Detail label="Send rate">
          {check.quota === null ? (
            <span className="text-muted">Not readable</span>
          ) : (
            formatSendRate(check.quota.maxSendRate)
          )}
        </Detail>
        <Detail label="Sent in the last 24 hours">
          {check.quota === null ? (
            <span className="text-muted">Not readable</span>
          ) : (
            formatSentLast24Hours(check.quota)
          )}
        </Detail>
        <Detail label="Verified identities">
          {/*
            Three readings, not two. "Not readable" is a different row from a
            count: `0 identities` says the account has no sender and is a setup
            problem, and printing it because the listing was refused sends
            somebody hunting a fault that is not there.

            When the listing was truncated these are a lower bound, not a count,
            and must not be rendered as though they were the whole account.
          */}
          {check.identities === null || verifiedIdentityCount === null ? (
            <span className="text-muted">Not readable</span>
          ) : (
            <>
              {check.identitiesTruncated ? 'at least ' : ''}
              {verifiedIdentityCount === check.identities.length
                ? pluralise(verifiedIdentityCount, 'identity', 'identities')
                : `${verifiedIdentityCount} of ${pluralise(check.identities.length, 'identity', 'identities')}`}
            </>
          )}
        </Detail>
      </dl>

      {check.identities !== null && check.identitiesTruncated && (
        <Callout tone="info" title="This is not the whole list">
          This account holds more identities than Wend reads in one check, so the list below is the
          first {check.identities.length} it returned rather than all of them. Nothing is wrong —
          but do not read the absence of a sender here as proof it is not verified.
        </Callout>
      )}

      {check.identities !== null && check.identities.length > 0 && (
        <IdentityList identities={check.identities} />
      )}
    </section>
  );
}

/**
 * Every identity on the account, verified or not.
 *
 * The unverified ones are listed rather than filtered out, because "I started
 * verifying that domain and never finished" is the most common reason an account
 * that looks set up cannot send, and it is invisible if the list only shows what
 * already works.
 */
function IdentityList({ identities }: { identities: readonly SesIdentity[] }) {
  return (
    <div className="mt-4 border-t border-line pt-3.5">
      <h4 className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
        Sending identities
      </h4>
      <ul className="mt-2 flex flex-col">
        {identities.map((identity) => (
          <li
            key={identity.identity}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line py-2.5 first:border-t-0 first:pt-0"
          >
            <span className="min-w-0 flex-1 truncate font-mono text-[0.85rem] text-text">
              {identity.identity}
            </span>
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
              {identityTypeLabel(identity.identityType)}
            </span>
            {identity.verified ? (
              <Badge tone="good">Verified</Badge>
            ) : (
              <Badge tone="warn">Not verified</Badge>
            )}
            {identity.verified && !identity.sendingEnabled && <Badge tone="bad">Paused</Badge>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Connect / replace                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The connect form, used for the first connection and for replacing one.
 *
 * SECURITY, and it is the reason this is written the way it is:
 *
 *   - the secret is a `type="password"` input with `autoComplete="off"`, so it is
 *     not echoed on screen and no stored value is offered into it. Nothing
 *     pre-fills it — there is nothing to pre-fill it *with*, because the API has
 *     no field a stored secret could come back in;
 *   - it lives in this component's state only while it is being typed, and it is
 *     cleared as soon as the request it was typed for has been answered, whether
 *     that answer was a success or a failure. It does not survive the submission;
 *   - it is sent in a JSON body. `connectSes` passes no query, so it never
 *     reaches a URL — and therefore never reaches browser history or a proxy log.
 *
 * The form validates against `connectSesRequestSchema`, the same contract the API
 * validates against, so a mistyped key is refused here without a round trip and
 * without the two ever disagreeing about what is acceptable.
 *
 * **Its two paragraphs of instruction stay on the form rather than moving
 * behind a disclosure.** They are only rendered for a first connection, they
 * are what somebody is holding the AWS console open to read, and the second one
 * names the two IAM actions this check needs — which is not background, it is
 * the step. The text rule makes instruction progressive; it does not make it
 * optional at the moment it is being followed.
 */
function ConnectForm({
  existing,
  onConnected,
  onCancel,
}: {
  existing: SesIntegration | null;
  onConnected: (response: SesCheckResponse) => void;
  onCancel?: () => void;
}) {
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState(existing?.region ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);

    const result = validate(connectSesRequestSchema, { accessKeyId, secretAccessKey, region });
    if (!result.ok) {
      // Nothing left the browser, so the secret has not been used yet and is
      // kept: clearing it here would punish a typo in the region field.
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    let response: SesCheckResponse;
    try {
      response = await connectSes(result.data);
    } catch (error) {
      // The server's per-field verdicts, folded in beside the client's own. Its
      // form-level message is dropped here because the alert below already shows
      // it, with the request id and any retry-after attached.
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure(requestFailure(error));
      // SECURITY: the request was made, so the secret has served its purpose and
      // does not outlive it. A retry means pasting it again — said on the form.
      setSecretAccessKey('');
      setPending(false);
      return;
    }

    setSecretAccessKey('');
    onConnected(response);
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      autoComplete="off"
      className="flex max-w-xl flex-col gap-5"
    >
      {existing === null && (
        <Callout tone="info" title="These are your own AWS credentials">
          <p>
            Every message counts against this account&rsquo;s quota and appears on your AWS bill,
            and anything AWS decides to do about the sending — a rate cut, a review, a suspension —
            lands on your AWS account rather than on Wend&rsquo;s.
          </p>
          <p className="mt-2">
            Create an IAM user access key that is allowed to call{' '}
            <span className="font-mono text-[0.82rem] text-text">ses:GetAccount</span> and{' '}
            <span className="font-mono text-[0.82rem] text-text">ses:ListEmailIdentities</span>;
            those two are what this check reads.
          </p>
        </Callout>
      )}

      {failure !== null && <RequestFailureAlert failure={failure} />}
      {errors[ROOT_FIELD] !== undefined && <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>}

      <FormField
        label="Access key ID"
        required
        description="A long-lived IAM user key. Temporary keys starting ASIA expire, and Wend re-checks and sends long after you close this page."
        error={errors.accessKeyId}
      >
        <Input
          value={accessKeyId}
          onChange={(event) => {
            setAccessKeyId(event.target.value);
            setErrors((current) => withoutError(current, 'accessKeyId'));
          }}
          autoComplete="off"
          spellCheck={false}
          placeholder="AKIAIOSFODNN7EXAMPLE"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Secret access key"
        required
        description="Encrypted before it is stored, and never shown again — not here, not by the API, not to Wend. It is cleared from this form the moment it is sent, so a retry means pasting it again."
        error={errors.secretAccessKey}
      >
        <Input
          // Never echoed, never pre-filled, and never offered a stored value.
          type="password"
          value={secretAccessKey}
          onChange={(event) => {
            setSecretAccessKey(event.target.value);
            setErrors((current) => withoutError(current, 'secretAccessKey'));
          }}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Region"
        required
        // Free text rather than a picker: AWS adds regions, and a list written
        // here would go stale and start refusing a region that genuinely exists.
        description="The region your SES account is in — the one in the SES console URL, for example us-east-1 or ap-south-1."
        error={errors.region}
      >
        <Input
          value={region}
          onChange={(event) => {
            setRegion(event.target.value);
            setErrors((current) => withoutError(current, 'region'));
          }}
          autoComplete="off"
          spellCheck={false}
          placeholder="us-east-1"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending}>
          {pending
            ? 'Asking Amazon'
            : existing === null
              ? 'Connect and test'
              : 'Replace and test'}
        </Button>

        {onCancel !== undefined && (
          <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        )}

        <p className="text-[0.85rem] text-muted">
          Saving runs a check against AWS straight away, so you find out here rather than at the
          first campaign.
        </p>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Disconnect                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Disconnecting, with the confirmation inline rather than in a dialog — the same
 * way deleting a segment does it, and for the same reason: what a confirmation
 * has to say is not "are you sure" but *what is lost*.
 *
 * Here that is genuinely a loss. Disconnecting deletes the sealed credential
 * rather than flagging the row, so Wend keeps no copy — and AWS cannot show a
 * secret access key a second time either, so reconnecting usually means creating
 * a new key rather than finding the old one. Someone who knows that can decide in
 * one step.
 *
 * **It is not behind Advanced,** and the dividing line is why: whether an
 * account is connected is the first thing this tab answers, and the control
 * that un-answers it belongs on the same surface as the answer. It is last, and
 * red, which is prominence enough for something done once.
 */
function DisconnectPanel({
  integration,
  onDisconnected,
}: {
  integration: SesIntegration;
  onDisconnected: () => void;
}) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function onDisconnect(): Promise<void> {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      await disconnectSes();
      toast({
        tone: 'success',
        title: 'Amazon SES disconnected',
        description: 'The stored credentials were deleted.',
      });
      onDisconnected();
    } catch (error) {
      setFailure(requestFailure(error));
      setPending(false);
    }
  }

  return (
    <Card className="border-bad/25">
      <CardHeader>
        <CardTitle className="text-[1rem]">Disconnect Amazon SES</CardTitle>
        <CardDescription>
          This deletes the stored credentials rather than switching them off, so Wend stops holding
          your key entirely. Your lists, segments and contacts are untouched — but nothing can be
          sent by email until an SES account is connected again.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failure !== null && <RequestFailureAlert failure={failure} />}

        {confirming ? (
          <div className="flex flex-col gap-3">
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              Disconnect the account in{' '}
              <span className="font-mono text-[0.88rem]">{integration.region}</span> using the key
              ending <span className="font-mono text-[0.88rem]">{integration.accessKeyIdLast4}</span>
              ? The sealed secret is deleted and Wend cannot recover it. AWS will not show you that
              secret again either, so reconnecting most likely means creating a new access key.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending}
                className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                onClick={() => {
                  void onDisconnect();
                }}
              >
                <Trash2 aria-hidden="true" className="size-4" />
                {pending ? 'Disconnecting' : 'Yes, disconnect and delete the key'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Keep it connected
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Disconnect
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
